import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, ArrowRight, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/auth-context';
import { TurnstileBox } from '@/components/TurnstileBox';
import { toast } from 'sonner';
import SEO from '@/components/SEO';

const Register: React.FC = () => {
  const { user, signup, loginWithGoogle } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreed: false,
    website: '' // Honeypot
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [showTurnstile, setShowTurnstile] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | undefined>(undefined);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const defaultRedirect = '/plans';
  const redirect = searchParams.get('redirect') || defaultRedirect;
  const verifyPath = '/verify-otp';

  React.useEffect(() => {
    if (user) {
      navigate(redirect);
    }
  }, [user, navigate, redirect]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Honeypot check
    if (formData.website) {
      navigate(`${verifyPath}?email=${encodeURIComponent(formData.email)}${redirect ? `&redirect=${encodeURIComponent(redirect)}` : ''}`);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!formData.agreed) {
      setError('You must agree to the Terms and Privacy Policy');
      return;
    }

    setLoading(true);

    try {
      const result = await signup(formData.email, formData.password, formData.username, turnstileToken);

      if (result && result.requiresCaptcha) {
        setShowTurnstile(true);
        setLoading(false);
        toast.info('Security verification required', {
          description: 'Please complete the challenge to continue.'
        });
        return;
      }

      navigate(`${verifyPath}?email=${encodeURIComponent(formData.email)}${redirect ? `&redirect=${encodeURIComponent(redirect)}` : ''}`);
    } catch (err: any) {
      setError(err.message || 'Registration failed');
      setTurnstileToken(undefined);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4 pt-40 pb-20 relative overflow-hidden transition-colors duration-1000">
      <SEO title="Sign Up" noindex />
       {/* Background accents */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-full h-full bg-[radial-gradient(circle_at_50%_0%,rgba(6,182,212,0.1)_0%,transparent_50%)]" />
      </div>

      <div className="w-full max-w-[480px] relative z-10">
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-5xl font-bold font-display text-cyan-950 tracking-tighter mb-3 uppercase">
             Sign Up
          </h1>
          <p className="text-zinc-600 font-medium italic text-sm">Create your account to join the playground.</p>
        </div>

        <div className="bg-white p-10 rounded-[2.5rem] border border-zinc-950/10 shadow-2xl shadow-black/5">
          <div className="mb-8">
            <button
              onClick={() => loginWithGoogle()}
              className="w-full flex items-center justify-center gap-3 px-4 h-14 border border-zinc-950/10 rounded-2xl bg-white text-zinc-700 hover:bg-zinc-50 hover:border-cyan-600/30 transition-all font-bold uppercase text-[11px] tracking-[0.1em] shadow-sm hover:shadow-md active:scale-[0.98] relative overflow-hidden group"
            >
              <div className="absolute inset-0 bg-linear-to-r from-cyan-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="relative z-10">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-1 .67-2.28 1.07-3.71 1.07-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.11c-.22-.66-.35-1.36-.35-2.11s.13-1.45.35-2.11V7.05H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.95l3.66-2.84z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84c.87-2.6 3.3-4.51 6.16-4.51z" fill="#EA4335"/>
              </svg>
              <span className="text-cyan-950 relative z-10">Sign up with Google</span>
            </button>
            <p className="mt-3 text-[9px] text-zinc-400 text-center font-medium uppercase tracking-tight px-4">
              By joining, you agree to our <Link to="/terms-of-service" className="underline">Terms</Link> & <Link to="/privacy-policy" className="underline">Privacy Policy</Link>
            </p>
          </div>

          <div className="relative mb-8 text-center">
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <div className="w-full border-t border-zinc-100"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="px-3 bg-white text-[10px] font-black uppercase tracking-widest text-zinc-400">
                Or sign up with email
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Honeypot field */}
            <div className="hidden" aria-hidden="true">
              <input 
                type="text" 
                name="website" 
                value={formData.website} 
                onChange={(e) => setFormData({...formData, website: e.target.value})} 
                tabIndex={-1} 
                autoComplete="off" 
              />
            </div>

            <div className="grid grid-cols-1 gap-5">
               <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-600 ml-1">Username</label>
                <div className="relative group/input">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500 transition-colors group-focus-within/input:text-cyan-600" />
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({...formData, username: e.target.value})}
                    className="block w-full pl-12 pr-4 py-4 bg-zinc-50/50 border border-cyan-100 rounded-2xl text-cyan-950 font-medium placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-cyan-600/5 focus:border-cyan-600 transition-all"
                    placeholder="Choose a username"
                  />
                </div>
              </div>

              <div className="space-y-2">
                 <label className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-600 ml-1">Email</label>
                <div className="relative group/input">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500 transition-colors group-focus-within/input:text-cyan-600" />
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="block w-full pl-12 pr-4 py-4 bg-zinc-50/50 border border-cyan-100 rounded-2xl text-cyan-950 font-medium placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-cyan-600/5 focus:border-cyan-600 transition-all"
                    placeholder="name@example.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-600 ml-1">Password</label>
                  <div className="relative group/input">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500 transition-colors group-focus-within/input:text-cyan-600" />
                    <input
                      type="password"
                      required
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      className="block w-full pl-12 pr-4 py-4 bg-zinc-50/50 border border-cyan-100 rounded-2xl text-cyan-950 font-medium placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-cyan-600/5 focus:border-cyan-600 transition-all"
                      placeholder="********"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-600 ml-1">Confirm</label>
                  <div className="relative group/input">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500 transition-colors group-focus-within/input:text-cyan-600" />
                    <input
                      type="password"
                      required
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                      className="block w-full pl-12 pr-4 py-4 bg-zinc-50/50 border border-cyan-100 rounded-2xl text-cyan-950 font-medium placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-cyan-600/5 focus:border-cyan-600 transition-all"
                      placeholder="********"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 px-1">
              <input 
                type="checkbox"
                id="agree"
                checked={formData.agreed}
                onChange={(e) => setFormData({...formData, agreed: e.target.checked})}
                className="w-4 h-4 rounded border-cyan-100 text-cyan-600 focus:ring-cyan-600 cursor-pointer"
              />
              <label htmlFor="agree" className="text-[11px] font-medium text-zinc-600 leading-tight cursor-pointer">
                I agree to the <Link to="/terms-of-service" className="text-cyan-600 font-bold hover:underline">Terms of Service</Link> and <Link to="/privacy-policy" className="text-cyan-600 font-bold hover:underline">Privacy Policy</Link>.
              </label>
            </div>

            <TurnstileBox 
              show={showTurnstile} 
              onVerify={(token) => setTurnstileToken(token)} 
            />

            {error && (
              <div className="p-4 bg-red-50 border border-red-100 rounded-2xl text-red-500 text-[10px] font-black uppercase tracking-widest text-center animate-in fade-in slide-in-from-top-2">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 bg-cyan-600 !text-white font-black uppercase tracking-[0.1em] rounded-2xl shadow-xl shadow-cyan-600/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-cyan-700"
            >
              {loading ? 'Signing up...' : (
                <>
                  SIGN UP <ArrowRight className="w-5 h-5 ml-1" />
                </>
              )}
            </Button>
          </form>
        </div>

        <div className="mt-8 pt-8 border-t border-cyan-100 text-center">
          <p className="text-zinc-500 text-sm font-medium">
            Have an account?{' '}
            <Link to="/login" className="font-bold text-cyan-600 hover:underline transition-all">
              Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
